import * as React from 'react';
import { Button, ButtonProps, IconButton } from '@mui/material';
import styled from 'styled-components';
import Icon from '@mui/material/Icon';
import { colors } from 'spw-theme';
import downloadIcon from 'spw-static/images/download.svg';
import whiteDownloadIcon from 'spw-static/images/download-white.svg';
import grayDownloadIcon from 'spw-static/images/download-gray.svg';
import { SPWSpinner } from 'spw-shared/globalComponents/spinners';
import { forwardRef } from 'react';
import { SPWCircularProgress } from '.';

const MainButton = forwardRef<HTMLButtonElement, Omit<ButtonProps, 'ref'>>(
  (props, ref) => <Button {...props} ref={ref} />
);

export const SPWMainButton = styled(MainButton)`
  && {
    color: white;
    border-radius: 3px;
    height: 36px;
    background-color: ${colors.mainBlue};
    white-space: nowrap;
  }

  &&:hover {
    opacity: 0.8;
    background-color: ${colors.mainBlue};
  }

  &&.Mui-disabled {
    color: ${colors.mainGray};
    background-color: ${colors.veryLightGray};
  }

  &&.width-88 {
    width: 88px;
    padding-left: 0;
    padding-right: 0;
  }

  &&.v-padded {
    padding-left: 16px;
    padding-right: 16px;
  }
`;

const OutlinedMainButton = forwardRef<
  HTMLButtonElement,
  Omit<ButtonProps, 'ref'>
>((props, ref) => <Button {...props} variant="outlined" ref={ref} />);

export const SPWOutlinedMainButton = styled(
  forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
    return <OutlinedMainButton {...props} ref={ref} />;
  })
)`
  border-radius: 3px;
  height: 36px;
  text-transform: uppercase;
  transform: null;

  && {
    border: 1px solid ${colors.mainBlue};
    color: ${colors.mainBlue};
  }

  &&.Mui-disabled {
    color: ${colors.mainGray};
    border-color: ${colors.veryLightGray};
  }

  &&.MuiButton-fullWidth {
    width: 100%;
  }
`;

export const SPWOutlineBlackButton = styled((props: ButtonProps) => (
  <Button {...props} variant="outlined">
    {props.children}
  </Button>
))`
  border-radius: 3px;
  min-width: 96px;
  height: 36px;
  text-transform: uppercase;
  transform: null;

  && {
    border: 1px solid ${colors.black};
    color: ${colors.black};
  }

  &&.Mui-disabled {
    color: ${colors.mainGray};
    border-color: ${colors.veryLightGray};
  }

  &&.width-88 {
    width: 88px;
  }
`;

export const SPWSaveButton = styled(
  ({
    disabled,
    loading = false,
    name,
    ...props
  }: ButtonProps & { loading?: boolean }) => {
    const content = loading ? (
      <SPWCircularProgress color={colors.mainBlue} thickness={5} />
    ) : (
      name || 'Save'
    );

    return (
      <SPWMainButton {...props} disabled={loading ? true : disabled}>
        {content}
      </SPWMainButton>
    );
  }
)`
  && {
    width: 96px;
  }

  &:hover {
    width: 96px;
  }

  &.MuiButton-fullWidth {
    width: 100%;
  }

  &.MuiButton-fullWidth:hover {
    width: 100%;
  }
`;

export const SPWSaveButton88Width = styled(SPWSaveButton)`
  && {
    width: 88px;
  }
`;

export const SPWCancelButton = styled(
  (props: ButtonProps & { customColor?: string }) => (
    <SPWOutlinedMainButton {...props} variant="outlined">
      {props.children ? props.children : 'Cancel'}
    </SPWOutlinedMainButton>
  )
).withConfig({
  shouldForwardProp: (prop) => !['customColor'].includes(prop),
})`
  ${(props: ButtonProps & { customColor?: string }) =>
    props.customColor
      ? `&&{color: ${props.customColor}; border-color: ${props.customColor}}`
      : ''}
  &&.Mui-disabled {
    color: ${colors.darkerGray};
  }

  min-width: 64px;

  &&.width-88 {
    width: 88px;
    min-width: 88px;
    padding-left: 0;
    padding-right: 0;
  }
`;

export const SPWCancelButton88Width = styled(SPWCancelButton)`
  && {
    width: 88px;
  }
`;

export const SPWDeleteButton = styled((props: ButtonProps) => (
  <Button {...props} variant="text">
    {props.children ? props.children : 'Delete'}
  </Button>
))`
  color: ${colors.red};
  background-color: transparent;
  height: 36px;

  &.margin-left-16 {
    margin-left: 16px;
  }
`;

export const SPWDeleteButton88Width = styled(SPWDeleteButton)`
  && {
    width: 88px;
    padding-left: 0;
    padding-right: 0;
  }
`;

export const SPWDeleteSpaceButton = styled(SPWDeleteButton)`
  && {
    padding: 6px 16px;
  }
`;

export const SPWHoverlessButton = styled(Button)`
  &&:hover {
    background-color: unset;
  }
`;

export const InvisibleButton = styled(Button)`
  && {
    min-width: 0px;
  }
`;

export const SPWCancelInvisibleButton = styled((props: ButtonProps) => (
  <InvisibleButton {...props} variant="text">
    {props.children ? props.children : 'Cancel'}
  </InvisibleButton>
))`
  && {
    color: ${colors.mainBlue};
    background-color: transparent;
  }
`;

export const SPWWarningButton = styled((props: ButtonProps) => (
  <Button {...props} variant="outlined">
    {props.children ? props.children : 'Yes, Cancel'}
  </Button>
))`
  && {
    border-radius: 4px;
    width: 96px;
    height: 40px;
    color: white;
    background-color: ${colors.red};
    text-transform: none;
    transform: null;
    border: 1px solid #000000;
  }

  &:hover {
    width: 96px;
    min-height: 40px;
  }
`;

export const SPWButtonAsText = styled((props: ButtonProps) => (
  <Button {...props} variant="text">
    {props.children}
  </Button>
))`
  && {
    font-weight: 500;
    font-size: 14px;
    color: ${colors.mainGreen};
    text-transform: uppercase;
  }

  &.Mui-selected {
    font-weight: 500;
    font-size: 14px;
    color: ${colors.mainGreen};
    text-transform: uppercase;
  }

  &.active {
    color: ${colors.fontColor};
  }
`;

export const SPWButtonAsTextLarge = styled((props: ButtonProps) => (
  <Button {...props} variant="text">
    {props.children}
  </Button>
))`
  && {
    font-weight: 500;
    font-size: 18px;
    color: ${colors.mainGreen};
    text-transform: uppercase;
  }

  &.Mui-selected {
    font-weight: 500;
    font-size: 14px;
    color: ${colors.mainGreen};
    text-transform: uppercase;
  }
`;

export const SPWButtonIconAndText = styled((props: ButtonProps) => (
  <Button {...props} variant="text">
    {props.children}
  </Button>
))`
  && {
    color: ${colors.mainBlue};
    font-weight: 500;
    font-size: 14px;
    ${(props: { $noBackgroundOnHover?: boolean }) =>
      props.$noBackgroundOnHover ? `background-color: initial;` : ''}
  }

  &.Mui-selected {
    color: ${colors.mainBlue};
    font-weight: 500;
    font-size: 14px;
  }

  &.Mui-disabled {
    color: ${colors.disabledIcons} !important;
    opacity: 0.75;
  }

  &.smaller {
    .MuiButton-label {
      margin-left: 0;

      .MuiButton-startIcon {
        margin-left: 0;
      }
    }

    span {
      font-size: 14px;
      margin-left: 0;
    }
  }
`;

export const SPWIconButton = styled(IconButton)`
  &.MuiIconButton-root {
    padding: 0;

    :hover {
      background-color: unset;
    }
  }
`;

const SPWButtonIconAndTextForExport = styled(SPWButtonIconAndText)`
  padding-left: 0;
  padding-right: 0;
  margin-left: 8px;

  && {
    background-color: initial;
  }

  &.Mui-disabled {
    span:not(.button-text),
    svg {
      color: ${colors.white} !important;
    }
  }

  &.MuiButton-root.Mui-disabled > span.button-text {
    color: ${colors.disabledIcons} !important;
    border-color: ${colors.disabledIcons} !important;
  }

  span.button-text {
    padding-top: 2px;
  }

  &.pt-0 {
    span.button-text {
      padding-top: 0;
      line-height: 24px;
    }
  }
`;

export interface SPWExportButtonProps {
  color?: string;
  disabled?: boolean;
  busy: boolean;
  onClick: (event?: React.BaseSyntheticEvent) => void;
  classes?: string;
  style?: React.CSSProperties;
  testingPrefix?: string;
}

export const SPWExportButton = ({
  color,
  disabled,
  busy,
  onClick,
  classes,
  style,
  testingPrefix,
}: SPWExportButtonProps) => {
  const iconColor = color || colors.mainBlue;
  const label = busy ? <SPWSpinner color={iconColor} /> : 'DOWNLOAD';
  let icon = downloadIcon;
  if (disabled && iconColor !== 'white') {
    icon = grayDownloadIcon;
  } else if (iconColor === 'white') {
    icon = whiteDownloadIcon;
  }

  return (
    <SPWButtonIconAndTextForExport
      disabled={disabled}
      className={classes}
      onClick={busy ? () => Function.prototype : onClick}
      style={{ ...style } || {}}
      data-testid={testingPrefix ? `${testingPrefix}_download_button` : ''}
      startIcon={
        !busy && (
          <Icon
            style={{
              color: iconColor,
              display: 'flex',
              alignContent: 'center',
              fontSize: classes?.includes('smaller') ? '14px' : '16px',
            }}
          >
            <img
              src={icon}
              alt="Download"
              style={{
                fontSize: classes?.includes('smaller') ? '14px' : '18px',
              }}
            />
          </Icon>
        )
      }
    >
      <span className="button-text">{label}</span>
    </SPWButtonIconAndTextForExport>
  );
};
